/** @format */

import {useTheme} from '@mui/material/styles';
import {Trans} from 'react-i18next';

import {shortNumber} from 'helpers/numberHelpers';
import {type MetricProgress} from 'features/mediaplan-progress/types';

import {type BuiltChartData} from '../buildChartData';
import {OverviewChart} from '../OverviewChart';
import {getValueColor} from '../KpiSection/getValueColor';
import {MetricTextBlock} from '../KpiSection/MetricTextBlock';
import {KpiRow, TextBlock} from '../KpiSection/KpiCharts/styles';

import {ChartArea, SpendLabel} from './styles';

// chartId namespace for the spend chart — single fixed series.
export type SpendChartId = 'spend';

// Recharts-ready data for the dynamic spend chart — exactly what buildChartData produces.
// Aliased to the single source so a new BuiltChartData field can't silently drop here.
export type SpendChartData = BuiltChartData<SpendChartId>;

interface Props {
  progress: MetricProgress;
  planPeriodLabel: string;
  chart?: SpendChartData;
  // false when <7 days of fact → the «Прогноз» row is hidden (AS-7886).
  showForecast: boolean;
}

// Money in compact form ("10 млн ₽"); ₽ postfix added in-component.
const formatMoney = (value: number): string => `${shortNumber(value)} ₽`;

/**
 * Расход column of the dynamic card body: a side-by-side [text | chart] row (mirrors a KPI row,
 * Figma node 10284:124880/125842). Text on the left (План на месяц / на сегодня / Факт /
 * Выполнение / Прогноз — no остаток in dynamic), chart on the right filling the column height.
 */
const SpendSection = ({progress, chart, planPeriodLabel, showForecast}: Props) => {
  const theme = useTheme();

  // Chart inherits the «Выполнение» color (fact vs plan; Расход is lower-is-better). No mediaplan
  // → undefined → default color.
  const factColor = getValueColor(progress.factToDate, progress.planToDate, true);
  const chartColor = progress.planForPeriod > 0 ? factColor(theme) : undefined;

  const renderChart = () => {
    const chartObj = chart?.data.charts[0];
    if (!chart || !chartObj) {
      return null;
    }

    return (
      <OverviewChart
        chart={chartObj}
        tooltipData={chart.data.tooltipDataList}
        height={chart.heightParams.boxHeight}
        axisStart={chart.axisStart}
        axisEnd={chart.axisEnd}
        todayRatio={chart.todayRatio}
        todayLabel={chart.todayLabel}
        valueLabels={chart.valueLabels}
        forecastValueLabels={chart.forecastValueLabels}
        color={chartColor}
      />
    );
  };

  return (
    <KpiRow>
      <TextBlock>
        <SpendLabel>
          <Trans>Расход</Trans>
        </SpendLabel>

        <MetricTextBlock
          progress={progress}
          // Расход — cost metric: spending over plan is bad.
          lowerIsBetter
          showPlanToDate
          showForecast={showForecast}
          planPeriodLabel={planPeriodLabel}
          formatValue={formatMoney}
        />
      </TextBlock>

      <ChartArea sx={{minHeight: chart?.heightParams.overallHeight}}>{renderChart()}</ChartArea>
    </KpiRow>
  );
};

export {SpendSection};
